import { useToast } from '@orbind/ui'
import { useMutacoesDeVisao, type VisaoNaTela } from './api.ts'
import './visao.css'

/**
 * ✓ / ✗ da visão EM EDIÇÃO (SPEC-20261104-2147, pedido do Cristian): mexer
 * na tela com uma visão escolhida (colunas, ordem, largura, árvore/lista,
 * modo, período, filtro) vira rascunho da pessoa, guardado no servidor.
 * Fica "em edição" até ✓ (grava na visão, para todos que a veem) ou ✗ (volta
 * ao salvo). Fechar o app não perde nada.
 *
 * ✓ numa visão de outra pessoa só para o dono ou um administrador; os demais
 * podem usar o rascunho e descartá-lo. Se a visão mudou depois que o
 * rascunho começou, o aviso deixa escolher: ✓ sobrescreve, ✗ descarta.
 */
export function EdicaoDaVisao({ quadroId, visao }: { quadroId: string | null; visao: VisaoNaTela | null }) {
  const { descartar, confirmar } = useMutacoesDeVisao(quadroId)
  const toast = useToast()
  if (!visao?.emEdicao) return null

  const ocupado = descartar.isPending || confirmar.isPending
  const podeConfirmar = visao.canCommit === true
  const erro = (e: unknown) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível concluir.' })

  return (
    <span className="visao-edicao" role="group" aria-label={`Visão ${visao.name} em edição`}>
      {visao.draftStale ? (
        <span
          className="visao-edicao__aviso"
          title="Outra pessoa alterou esta visão depois que você começou a editar. ✓ grava a sua versão por cima; ✗ descarta a sua e mostra a atual."
          aria-label="A visão foi alterada por outra pessoa"
        >
          <i className="fas fa-exclamation-triangle" aria-hidden />
        </span>
      ) : null}
      <span className="visao-edicao__rotulo">em edição</span>
      <button
        type="button"
        className="visao-edicao__btn visao-edicao__btn--ok"
        aria-label="Confirmar as alterações na visão"
        title={
          podeConfirmar
            ? visao.visibility === 'private'
              ? 'Gravar as alterações na visão'
              : 'Gravar as alterações na visão — passam a valer para todos que a veem'
            : 'Só o dono da visão ou um administrador grava para todos. Use ✗ para descartar a sua edição.'
        }
        disabled={!podeConfirmar || ocupado}
        onClick={() =>
          confirmar.mutate(visao.id, {
            onSuccess: () => toast.push({ tone: 'success', message: 'Visão atualizada.' }),
            onError: erro,
          })
        }
      >
        <i className="fas fa-check" aria-hidden />
      </button>
      <button
        type="button"
        className="visao-edicao__btn visao-edicao__btn--cancel"
        aria-label="Descartar as alterações da visão"
        title="Descartar as alterações e voltar ao que estava salvo"
        disabled={ocupado}
        onClick={() =>
          descartar.mutate(visao.id, {
            onSuccess: () => toast.push({ tone: 'info', message: 'Alterações descartadas.' }),
            onError: erro,
          })
        }
      >
        <i className="fas fa-times" aria-hidden />
      </button>
    </span>
  )
}
